import React, { useState } from 'react';
import { Send, Mail, User, MessageSquare } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { toast } from 'sonner';

/* --------------------------------------------------
   CONFIG
-------------------------------------------------- */
const CONTACT_API_URL =
  'https://nt-studios-ai-tools-3f06.onrender.com/api/contact';

/* --------------------------------------------------
   COMPONENT
-------------------------------------------------- */
const Contact = () => {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [formRef, formVisible] = useScrollAnimation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectIdea: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        throw new Error('Submission failed');
      }

      toast.success('Message Sent!', {
        description: "Thank you for reaching out. We'll get back to you soon.",
      });

      setFormData({ name: '', email: '', projectIdea: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Submission Failed', {
        description: 'Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-large">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${
            headerVisible ? 'visible' : ''
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get In Touch
          </h2>
          <div className="w-20 h-1 bg-accent-teal mx-auto mb-6"></div>
          <p className="text-lg text-neutral-silver max-w-2xl mx-auto">
            Have a project in mind? Let's discuss how we can bring your vision to life
          </p>
        </div>

        {/* Contact Form */}
        <div
          ref={formRef}
          className={`max-w-2xl mx-auto scroll-animate-scale ${
            formVisible ? 'visible' : ''
          }`}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Your Name"
              icon={User}
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
            />

            <Input
              label="Email Address"
              icon={Mail}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
            />

            <Textarea
              label="Project Idea / Discussion Topic"
              icon={MessageSquare}
              name="projectIdea"
              value={formData.projectIdea}
              onChange={handleChange}
              placeholder="Tell us about your project idea or what you'd like to discuss..."
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
              <Send size={20} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------
   INPUT COMPONENTS
-------------------------------------------------- */
const Input = ({
  label,
  icon: Icon,
  name,
  value,
  onChange,
  placeholder,
  type = 'text',
}) => (
  <div>
    <label className="block text-white font-medium mb-2 flex items-center gap-2">
      <Icon size={18} className="text-accent-teal" />
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required
      placeholder={placeholder}
      style={{
        backgroundColor: 'rgba(109, 90, 137, 0.1)',
        borderColor: 'rgba(109, 90, 137, 0.3)',
        color: 'white',
      }}
      className="w-full px-4 py-3 rounded-lg border text-white placeholder-neutral-silver/50 focus:outline-none focus:border-accent-teal transition-colors"
    />
  </div>
);

const Textarea = ({
  label,
  icon: Icon,
  name,
  value,
  onChange,
  placeholder,
}) => (
  <div>
    <label className="block text-white font-medium mb-2 flex items-center gap-2">
      <Icon size={18} className="text-accent-teal" />
      {label}
    </label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      required
      rows={6}
      placeholder={placeholder}
      style={{
        backgroundColor: 'rgba(109, 90, 137, 0.1)',
        borderColor: 'rgba(109, 90, 137, 0.3)',
        color: 'white',
      }}
      className="w-full px-4 py-3 rounded-lg border text-white placeholder-neutral-silver/50 focus:outline-none focus:border-accent-teal transition-colors resize-none"
    />
  </div>
);

export default Contact;
